import {
  Box,
  Grid,
  Typography
} from "@mui/material";
import Comment from "../Comment";
import Content from "../Content";
import ImageSlider from "../ImageSlider";
import styles from './index.module.css'
import PricingTable from "../../PricingTable";
import { formatDocumentProductTitle } from "../../../utils/documentProductTitle";
import ArtWorkDetails, { resolveSalesOrderImprintType } from "../ArtWorkDetails"; 
import { isCustomerFacingPriceRow, isCustomerFacingVisibleLine } from "../../../utils/totals";
import { resolveAdditionalCostFlags } from "../../../utils/pricingEngine";

const toCents = (n) => Math.round((Number(n) || 0) * 100);
const fromCents = (c) => (Number(c) || 0) / 100;
const allocateByQuantityExact = (quantities = [], totalCents = 0) => {
  const safeTotal = Math.max(0, Number(totalCents) || 0);
  const q = quantities.map((n) => Math.max(0, Number(n) || 0));
  const totalUnits = q.reduce((sum, n) => sum + n, 0);
  if (!safeTotal || !totalUnits) return q.map(() => 0);

  const basePerUnit = Math.floor(safeTotal / totalUnits);
  let remainder = safeTotal - (basePerUnit * totalUnits);
  const allocated = q.map((qty) => qty * basePerUnit);

  for (let i = 0; i < q.length && remainder > 0; i += 1) {
    const extra = Math.min(remainder, q[i]);
    allocated[i] += extra;
    remainder -= extra;
  }
  return allocated;
};

const ProductDetails = ({
  productData,
  currency,
  estimationId,
  handleComments,
  salesOrderStatus,
  canComment = false,
  isEstimateView = false,
  proofDecisionContext,
  showPerLocationProofApproval = true,
  hasCompleteApprovalPackage = false,
  onProofDecisionUpdated,
  compactGeneratedSalesOrder = false,
  commentSavePending = false,
}) => {
  const { Images, ImageUrl, Imprinting, Name, Description, cartDetails, decorations, productAdditionalCharges, artworks:ArtWorks, taxRate } = productData

  const sizes = Imprinting?.Sizes?.Values?.map((item) => item?.Name)
  const colors = Imprinting?.Colors?.Values?.map((item) => item?.Name)
  console.log(cartDetails)
  
  const totalQuantity = cartDetails?.reduce((acc, item) => acc + item.Quantity, 0);

  const embeddedIncludeByRow = (() => {
    const rows = Array.isArray(cartDetails) ? cartDetails : [];
    const rowQuantities = rows.map((item) => Number(item?.Quantity) || 0);
    const fallbackQty = rowQuantities.reduce((sum, qty) => sum + qty, 0);
    if (!fallbackQty) return rowQuantities.map(() => 0);

    const processedIncludeKeys = new Set();
    const includeTotalCents = (decorations || []).reduce((sum, decoItem) => {
      const charges = decoItem?.Charge || [];
      return sum + charges.reduce((acc, ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        if (!flags.includeInPrice) return acc;
        const sharedKey = String(ch?.sharedGroupKey ?? "").trim();
        if (sharedKey) {
          const dedupeKey = `${sharedKey}|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
          if (processedIncludeKeys.has(dedupeKey)) return acc;
          processedIncludeKeys.add(dedupeKey);
        }
        const retailC = toCents(ch?.Retail ?? ch?.retail);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const multiplier = flags.costType === "fixed"
          ? 1
          : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : fallbackQty);
        return acc + (retailC * multiplier);
      }, 0);
    }, 0);
    const productIncludeTotalCents = (productAdditionalCharges || []).reduce((sum, ch) => {
      const flags = resolveAdditionalCostFlags(ch);
      if (!flags.includeInPrice) return sum;
      const retailC = toCents(ch?.Retail ?? ch?.retail);
      const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
      const multiplier = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : fallbackQty);
      return sum + (retailC * multiplier);
    }, 0);
    return allocateByQuantityExact(rowQuantities, includeTotalCents + productIncludeTotalCents);
  })();

  const displayCartRows = (Array.isArray(cartDetails) ? cartDetails : []).map((item, idx) => {
    const quantity = Number(item?.Quantity) || 0;
    const basePriceC = toCents(item?.Price);
    const embeddedC = Number(embeddedIncludeByRow?.[idx]) || 0;
    const totalC = (basePriceC * quantity) + embeddedC;
    const perUnit = quantity > 0 ? fromCents(totalC / quantity) : fromCents(basePriceC);
    return {
      ...item,
      Price: Number(perUnit.toFixed(2)),
      Quantity: quantity,
      __embeddedAmount: fromCents(totalC),
    };
  });

  const priceDetails = () => {
    let PricesArray = []
   

    PricesArray = displayCartRows?.map((item) => {
      return {
        item:`${item.size || ''}${item.size && item.color ? ' - ' : ''}${item.color || ''}`,
        price: item.Price,
        quantity: item.Quantity,
        amount: parseFloat((Number(item?.__embeddedAmount) || 0).toFixed(2))
      };
    });
    const extraRows = (productAdditionalCharges || [])
      .filter((ch) => isCustomerFacingVisibleLine(ch))
      .map((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const quantity = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
        const price = Number(ch?.Retail ?? ch?.retail) || 0;
        return { item: ch?.Name || ch?.CostName || "Additional Cost", price, quantity, amount: +(price * quantity).toFixed(2) };
      });

    PricesArray = [
      ...PricesArray,
      ...extraRows,
      {
        item: 'Total Units',
        price: '',
        quantity: totalQuantity,
        amount: ''
      },
      {
        item: 'Total',
        price: '',
        quantity: '',
        amount: `${mainPricingTotal.toFixed(2)} ${currency}` || ''
      }
    ]
    return PricesArray
  }

const productExtraTotal = (productAdditionalCharges || []).reduce((sum, ch) => {
  if (!isCustomerFacingVisibleLine(ch)) return sum;
  const flags = resolveAdditionalCostFlags(ch);
  const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
  const quantity = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
  return sum + ((Number(ch?.Retail ?? ch?.retail) || 0) * quantity);
}, 0);

const totalDecorationAmount = decorations?.reduce((sum, decoItem) => {
  const charges = decoItem?.Charge || [];
  const itemTotal = charges.reduce((chargeSum, item) => {
    if (!isCustomerFacingPriceRow(item)) return chargeSum;
    const flags = resolveAdditionalCostFlags(item);
    const qtyRaw = Number(item?.Quantity ?? item?.quantity);
    const multiplier = flags.costType === "fixed"
      ? 1
      : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
    return chargeSum + ((Number(item?.Retail) || 0) * multiplier);
  }, 0);
  return sum + itemTotal;
}, 0);

const productTotal = displayCartRows.reduce((sum, row) => sum + (Number(row?.__embeddedAmount) || 0), 0);
const decorationTotal = parseFloat(totalDecorationAmount) || 0;
const rate = parseFloat(taxRate?.rate) || 0;
const decos = Array.isArray(decorations) ? decorations : []
const artArr = Array.isArray(ArtWorks) ? ArtWorks : []
const getArtworkForDecoration = (decoration, idx) => {
  const nestedArtwork = decoration?.ArtWork || decoration?.artwork || null;
  const decorationId = decoration?.id != null ? String(decoration.id) : null;
  const matchedArtwork = decorationId
    ? artArr.find((aw) => String(aw?.DecorationId ?? aw?.decorationId ?? "") === decorationId)
    : null;
  const indexedArtwork = artArr[idx] || null;
  const indexedDecorationId = indexedArtwork?.DecorationId ?? indexedArtwork?.decorationId;
  // Index alignment is only a legacy fallback when it cannot override an
  // explicitly identified decoration's own artwork with an unrelated row.
  const indexedMatchesDecoration = decorationId && indexedDecorationId != null
    && String(indexedDecorationId) === decorationId;
  const hydratedArtwork = matchedArtwork || (
    nestedArtwork ? (indexedMatchesDecoration ? indexedArtwork : null)
      : (!decorationId || indexedDecorationId == null || indexedMatchesDecoration ? indexedArtwork : null)
  );

  // Preserve a snapshot's artwork unless the enriched row has the same
  // decoration identity. This protects older documents with reordered arrays.
  if (nestedArtwork && hydratedArtwork) {
    return { ...nestedArtwork, ...hydratedArtwork };
  }
  return hydratedArtwork || nestedArtwork || null;
};
const artworkTotal = decos.reduce((sum, decoration, idx) => {
  const aw = getArtworkForDecoration(decoration, idx);
  const charge = parseFloat(aw?.Charge) || 0;
  if (charge <= 0) return sum;
  return sum + (charge * (parseFloat(totalQuantity) || 0));
}, 0);

const overallTotal = productTotal + artworkTotal + decorationTotal + productExtraTotal;
const mainPricingTotal = productTotal + productExtraTotal;
const firstFinite = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};
const taxBreakdown = [productData?.taxBreakdown, productData?.totals?.taxBreakdown]
  .find(Array.isArray) || [];
const authoritativeSubtotal = firstFinite(
  productData?.subtotal,
  productData?.Subtotal,
  productData?.totals?.preTax,
  productData?.totals?.subtotal
);
const authoritativeTaxAmount = firstFinite(
  productData?.taxAmount,
  productData?.TaxAmount,
  productData?.totals?.tax,
  productData?.totals?.taxAmount
);
const breakdownHasAmounts = taxBreakdown.length > 0 && taxBreakdown.every((row) =>
  firstFinite(row?.taxAmount, row?.amount) !== null
);
const breakdownTaxCents = breakdownHasAmounts
  ? taxBreakdown.reduce((sum, row) => sum + toCents(firstFinite(row?.taxAmount, row?.amount)), 0)
  : null;
const authoritativeTaxableAmount = firstFinite(
  productData?.taxableAmount,
  productData?.totals?.taxableAmount
);
const subtotalCents = authoritativeSubtotal !== null
  ? toCents(authoritativeSubtotal)
  : toCents(overallTotal);
const taxCents = authoritativeTaxAmount !== null
  ? toCents(authoritativeTaxAmount)
  : breakdownTaxCents !== null
    ? breakdownTaxCents
    : authoritativeTaxableAmount !== null
      ? Math.round(toCents(authoritativeTaxableAmount) * rate)
      : Math.round(subtotalCents * rate);
const authoritativeTotalInclTax = firstFinite(
  productData?.totalInclTax,
  productData?.totalIncl,
  productData?.totals?.totalInclTax,
  productData?.totals?.totalIncl
);
const grandTotalCents = authoritativeTotalInclTax !== null
  ? toCents(authoritativeTotalInclTax)
  : subtotalCents + taxCents;
const displaySubtotal = fromCents(subtotalCents);
const displayTax = fromCents(taxCents);
const displayGrandTotal = fromCents(grandTotalCents);

// Status check + whether comments enabled
const statusCode = salesOrderStatus != null ? String(salesOrderStatus) : '';
const baseStatusCode = statusCode.split('_')[0];
const canInteract = canComment && baseStatusCode === '1';   // Estimate (draft/sent) + commentsEnabled
const hasExisting = !!productData?.comment;

return (
    <Grid component="section" container spacing={3} className={`${styles.presentationCardContainer} ${styles.printProductSection}`}>

      <Grid className={styles.productHeading} style={{ padding: '12px' }}>
        <Content title={formatDocumentProductTitle(Name)} description={Description} type={'Text'} />
      </Grid>
      <Grid container spacing={3} className={styles.presentationCard}>
        <Grid item xs={12} md={4} className={styles.ImageContainer} style={{ padding: '12px' }}>
          <ImageSlider productImage={Images} mainImage={ImageUrl} useImageCDN={false} />
        </Grid>

        <Grid item xs={12} md={7} style={{ paddingLeft: '0px', paddingTop: '0px' }}>
          <Box>


            {colors?.length > 0 && <Content title={'Color'} description={colors?.join(', ')} type={'Text'} />}

            {sizes?.length > 0 && <Content title={'Sizes'} description={sizes?.join(', ')} type={'Text'} />}
            <Box className={styles.pricingTable}><PricingTable priceDetails={priceDetails()} /></Box>
             <Grid item xs={12} md={7} style={{ paddingLeft: '0px', paddingTop: '0px', minWidth: '100%' }}>

        
             {decos.length > 0 && decos.map((decoration, idx) => {

            const ArtWorkChargesArray = (decoration?.Charge || [])
              .filter((item) => isCustomerFacingVisibleLine(item))
              .map((item) => {
                const flags = resolveAdditionalCostFlags(item);
                const qtyRaw = Number(item?.Quantity ?? item?.quantity);
                const quantity = flags.costType === "fixed"
                  ? 1
                  : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
                return {
                  decoration: decoration?.Location,
                  item: item.Name,
                  price: item.Retail,
                  quantity,
                  amount: (Number(item?.Retail) || 0) * quantity
                };
              });
            
            const aw = getArtworkForDecoration(decoration, idx);
            const decorationMethodName =
              decoration?.DecoratorDetails?.methodName ??
              decoration?.decoratorDetails?.methodName ??
              decoration?.routing?.methodName ??
              decoration?.documentDecoration?.method?.name ??
              "";
            const displayImprintType = resolveSalesOrderImprintType(
              aw || {},
              decorationMethodName
            );
            const artUnitCharge = parseFloat(aw?.Charge) || 0;
            if (artUnitCharge > 0) {
              const artworkCost = {
                decoration: '',
                item: `Imprint Cost${displayImprintType && displayImprintType !== "-" ? ' - ' + displayImprintType : ''}`,
                price: artUnitCharge,
                quantity: totalQuantity,
                amount: artUnitCharge * (parseFloat(totalQuantity) || 0)
              };
              ArtWorkChargesArray.push(artworkCost);
            }
            
            return (
              <>


              <div key={idx}>
                {/* Costs for this decoration */}
                {ArtWorkChargesArray.length > 0 && (
                  <Box className={styles.pricingTable}><PricingTable priceDetails={ArtWorkChargesArray} isPo isArtWork /></Box>
                )}

                {/* Artwork Details for this decoration */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <div className={`${styles.numberedSection} ${styles.artworkHeading}`}>
                    <Typography variant="h6">
                      Artwork Details — {decoration?.Location || `Location ${idx  + 1}`}
                    </Typography>
                  </div>
                  <ArtWorkDetails
                    artwork={aw || {}}
                    location={decoration?.Location}
                    decorationProvider={decoration?.documentDecoration?.provider?.name || null}
                    decorationMethodName={decorationMethodName}
                    proofRow={aw?.proofReplacement?.proofRow}
                    proofMappingId={aw?.proofReplacement?.mappingId}
                    proofReplacement={aw?.proofReplacement}
                    proofDecisionContext={proofDecisionContext}
                    showPerLocationProofApproval={showPerLocationProofApproval}
                    hasCompleteApprovalPackage={hasCompleteApprovalPackage}
                    onProofDecisionUpdated={onProofDecisionUpdated}
                    compactGeneratedSalesOrder={compactGeneratedSalesOrder}
                  />
                </Grid>
              </div>

              </>
            )
          })}

        </Grid>
          
        {(canInteract || hasExisting) && (
          <Comment
            hideInPrint={isEstimateView}
            handleComments={handleComments}
            productData={productData}
            estimationId={estimationId}
            saving={commentSavePending}
          />
        )}




            <Box className={styles.productTotals} sx={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #ccc', marginTop:2, paddingTop:3}}>
                <Box sx={{ visibility: 'hidden' }}>.</Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography component="div">
                    Product subtotal (before tax): {displaySubtotal.toFixed(2)} {currency}
                  </Typography>
                  <Typography component="div" variant="body2">
                    Product tax ({(rate * 100).toFixed(2)}%): {displayTax.toFixed(2)} {currency}
                  </Typography>
                  <Typography component="div" sx={{ fontWeight: 700 }}>
                    Product total (incl. tax): {displayGrandTotal.toFixed(2)} {currency}
                  </Typography>
                </Box>
            </Box>
            

          </Box>
        </Grid>
      </Grid>

    </Grid>
  );
};

export default ProductDetails;
